/** Plan phase 1b — the Stage dropdown shows ONE ladder, chosen by the record's `deal_type`.
 *
 * Owner ruling 2026-09-01 (`docs/runbooks/portco-activation.md`, step 8): *"I don't want one super
 * long stage list spanning both new investments and exits."* An Exit record is offered the exit
 * ladder, everything else the buy-side one, and the record's own value is never hidden from it.
 *
 * ## What this module is NOT
 *
 * It is a PRESENTATION filter and nothing else. The field's stored options are untouched, the admin
 * options editor (`CrmColumnsPanel` / `CrmOptionsEditor` / the grid's options textarea) keeps
 * showing all of them, the set filter keeps offering all of them, and the backend's validator and
 * the classifier's stage enum still read the full option set. That is what lets it ship BEFORE the
 * runbook appends the nine exit options — and it is why the rules below never *remove* an option
 * from anything except one dropdown's visible rows.
 *
 * ## The three rules, and why each one exists
 *
 *  1. **The current value always survives.** A record sitting on a stage outside its own ladder —
 *     mid-retype, mis-typed `deal_type`, or an exit that has not been moved onto an `Exit:` stage
 *     yet — must still see the truth of what it holds, and must still be able to re-select it.
 *  2. **An unmapped option stays on the buy-side ladder.** Membership comes from
 *     `stage_mapping.stage_set`; a stage the table does not map reads as `default_set`. The failure
 *     direction matters: an unmapped option is shown to the majority ladder rather than hidden from
 *     everybody, so a mapping gap can never make a stage unreachable.
 *  3. **An empty ladder falls back to the whole list.** Before the runbook's step 8 runs, an Exit
 *     record's ladder is genuinely empty. With a current value that is fine (rule 1 leaves it
 *     selectable, which the runbook explicitly expects). With a BLANK cell it would leave nothing to
 *     pick — a record wedged out of ever getting a stage — so the unfiltered list comes back
 *     instead. This is the one rule that trades the ruling for not breaking a record, deliberately.
 */
import type { CrmStageLadders } from '../../types';

/** deal_type is matched case-insensitively and trimmed; the stage VALUE never is (an option may
 *  legitimately carry a trailing space — see the runbook's `'Exit: Signed '` near-duplicate). */
const dealTypeKey = (v: unknown): string => (v == null ? '' : String(v).trim().toLowerCase());

/** The record's deal type, off a grid row or a record's field values. `undefined` whenever the
 *  contingency does not apply, which `stageLadderOptions` reads as "show the buy-side ladder". */
export function dealTypeOf(
  values: Record<string, unknown> | null | undefined,
  ladders: CrmStageLadders | null | undefined,
): unknown {
  return ladders && values ? values[ladders.deal_type_field_key] : undefined;
}

/**
 * The options ONE record's Stage editor should offer.
 *
 * Returns `options` byte-identical for every field that is not the Stage field, and whenever the
 * server sent no ladder block — so every other select editor on every other list is unchanged.
 */
export function stageLadderOptions(
  fieldKey: string,
  options: string[] | undefined,
  /** the record's current stage value, kept selectable even when it is off-ladder */
  currentValue: unknown,
  /** the record's `deal_type` cell (see `dealTypeOf`) */
  dealType: unknown,
  ladders: CrmStageLadders | null | undefined,
): string[] | undefined {
  if (!ladders || fieldKey !== ladders.field_key || !options?.length) return options;
  const wantExit = dealTypeKey(dealType) === ladders.exit_deal_type;
  const visible = options.filter(
    (o) => ((ladders.sets[o] ?? ladders.default_set) === ladders.exit_set) === wantExit,
  );
  const current = currentValue == null ? '' : String(currentValue);
  // rule 1 — first, so it reads as the record's own value rather than as a ladder member
  if (current && !visible.includes(current)) return [current, ...visible];
  // rule 3 — only reachable with a blank cell AND an empty ladder
  if (!visible.length) return options;
  return visible;
}
